import React, { useMemo } from "react";

import { HardnessItem } from "../../forms/custom-hardness/hardnessResultSummaryForm";
import { HardnessEquipment } from "../../forms/custom-hardness/hardnessEquipmentForm";

import CustomCoverPage from "../custom-general/custom-cover-page";
import CustomInfoSheet from "../custom-general/custom-info-sheet";
import Conditional from "../../common/conditional";
import CustomGeneralPhotos from "../custom-general/custom-general-photos";
import ExecutiveSummary from "../custom-visual-inspection/executive-summary";
import IsometricDrawing from "../custom-utm/isometric-drawing";
import HardnessResultSummary from "./hardnessResultSummary";
import HardnessEquipmentReport from "./hardnessEquipmentReport";
import HardnessResultReport from "./hardnessResultReport";
import PID from "../custom-visual-inspection/pid";
import OverviewSheet from "../custom-general/overview-sheet";

import withPDF from "../../hoc/withPDF";
import useCoverSheetOverview from "../../../hooks/useCoverSheetOverview";

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

interface Props {
    projectDetails: models.Project;
	reportTitle: string;
    coverData: models.CustomFVIData;
    executiveSummary: string;
	pidData: models.PIDData;
	sketch: models.PDFImageProps[]
	hardnessEquipmentItems: HardnessEquipment[];
	hardnessResultItems: HardnessItem[];
	photographicData: models.PDFImageProps[];
	remark: string;
}

const CustomHardnessReport: React.FC<Props> = ({ executiveSummary, projectDetails, hardnessEquipmentItems, hardnessResultItems, pidData, photographicData, sketch, coverData, remark, reportTitle }) => {
    const { inspectionTypeName } = projectDetails;

	const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: coverData?.scopeOfWork,
		},
		{
			label: '2. VT Procedure.',
			value: coverData?.vtProcedure,
		},
		{
			label: '3. Rope Access Code of Practice.',
			value: coverData?.ropeAccessCodeOfPractice,
		},
		{
			label: '4. Work Procedure.',
			value: coverData?.exxonMobilWorkProcedure,
		},
		{
			label: '5. Arridex Procedure.',
			value: coverData?.russelsmithProcedure ?? '',
		},
	];

	const standardCriteriaItems: models.CoverDataItem[] = useMemo(() => {
		return coverData.coverData.map((item, index) => ({
			label: `${index + 1}. ${item.id}`,
			value: item.name.toString(),
		}));
	}, [coverData.coverData]);

	const overview = useCoverSheetOverview();
	const inspectionTypeLabel = utils.getExtractedInspectionName(inspectionTypeName);

	return (
		<React.Fragment>
			<CustomCoverPage reportTitle={inspectionTypeLabel} />

			<CustomInfoSheet
				projectDetails={projectDetails}
				workOrderNumber={coverData.workOrderNumber}
				reportTitle={reportTitle}
			/>

			<OverviewSheet
				overviewData={overview}
				referenceDocuments={referenceDocuments}
				standardCriteriaItems={standardCriteriaItems}
				projectDetails={projectDetails}
			/>

			<ExecutiveSummary summary={executiveSummary} />

			<HardnessResultSummary hardnessResultItems={hardnessResultItems} />

			{pidData.pIDImages.map((pid, index: number) => (
				<PID
					hasPID={pidData.hasPID}
					key={index} 
					pidImages={[pid]} 
					pidNumber={pidData.pIDNumber} 
					sectionTitle="P & ID"
				/>
			))}

			<IsometricDrawing label="Inspection Sketch" isoImages={sketch} />
            
			<HardnessEquipmentReport hardnessEquipmentItems={hardnessEquipmentItems} />

			<HardnessResultReport remark={remark} data={hardnessResultItems} />

			<Conditional isVisible={photographicData.length > 0}>
				<CustomGeneralPhotos
					photos={photographicData}
					label="Photographic Record"
				/>
			</Conditional>
        </React.Fragment>
	);
};

export default withPDF(CustomHardnessReport);